import React from "react";
import {
  StyledIconButton,
  PrimaryButton,
} from "../../components/StyledComponents/Buttons/AuradineButtons.js";
import {
  StyledDialog,
  StyledDialogTitle,
} from "../../components/StyledComponents/Dialog/Dialog.js";
import {
  H5HeadlineMedium,
  H8TitleMedium,
  BodyMedium,
} from "../../components/StyledComponents/Typography/Typography.tsx";
import {
  CircularProgress,
  DialogContent,
  FormControl,
  Stack,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { StyledFormControl } from "../../components/StyledComponents/Inputs/Inputs.js";
import { Controller, useForm } from "react-hook-form";
import CloseIcon from "@mui/icons-material/Close";
import { Box } from "@mui/material";
import {
  StyledTextField,
} from "../../components/StyledComponents/Inputs/Inputs.js";
import useAppContextProvider from "../../AppContext/useAppContextProvider.js";
import { useCaseCommands } from "../queries/useCaseCommands.ts";
import { useTheme } from "../../ThemeContext/ThemeContext.js";

const AddCommentModal = (props) => {
  const { open, setOpen, refetch, setTableKey, selectedCases, caseData } = props;
  const { setToastMessage, setToastOpen } = useAppContextProvider();
  const { colors } = useTheme();
  const {
    mutate: sendCaseCommands,
    isPending,
    isSuccess,
  } = useCaseCommands();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      comment: "",
      generateTechsupport: false, // Default to unchecked
    },
  });

  const commentValue = watch("comment");
  const generateTechsupportValue = watch("generateTechsupport");

  const onSubmit = (data) => {
    const { comment, generateTechsupport } = data;

    // Add comment command
    sendCaseCommands(
      {
        commands: [
          {
            command: "add-comment",
            caseIds: selectedCases,
            comment: comment,
            generateTechsupport: generateTechsupport || false,
            serials: hasSerials ? selectedSerials : [],
          },
        ],
      },
      {
        onSuccess: async (response) => {
          if (response.status === "ok") {
            await refetch();
            setTableKey((prev) => prev + 1);
            setOpen(false);
            setToastMessage("Comment added successfully");
            setToastOpen(true);
            reset();
          } else {
            setOpen(false);
            setToastMessage(`Error adding comment: ${response.Msg}`);
            setToastOpen(true);
          }
        },
        onError: (error) => {
          setToastMessage("Error adding comment");
          setToastOpen(true);
        },
      }
    );
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  // Get the selected cases details
  const selectedCasesData = caseData?.filter(caseItem => selectedCases.includes(caseItem.caseId));

  // Extract serial numbers from selected cases
  const selectedSerials = React.useMemo(() => {
    if (!selectedCasesData || selectedCasesData.length === 0) return [];
    const serials = selectedCasesData
      .map(caseItem => caseItem?.serialNo)
      .filter(serialNo => serialNo && serialNo.trim() !== "");
    // Remove duplicates and return unique serials
    return [...new Set(serials)];
  }, [selectedCasesData]);

  // Check if there are any serials available AND user selected <= 5 rows
  const hasSerials = selectedSerials.length > 0 && selectedCases.length <= 5;

  return (
    <StyledDialog
      colors={colors}
      maxWidth={"md"}
      fullWidth
      open={open}
      hideBackdrop
    >
      <StyledDialogTitle sx={{ paddingTop: 0 }}>
        <Box
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"space-between"}
        >
          <H5HeadlineMedium style={{ textAlign: "left" }}>
            Add Comment
          </H5HeadlineMedium>
          <StyledIconButton
            colors={colors}
            onClick={handleClose}
            style={{ textAlign: "right" }}
          >
            <CloseIcon />
          </StyledIconButton>
        </Box>
      </StyledDialogTitle>
      <DialogContent sx={{ padding: 0 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            display={"flex"}
            flexDirection={"column"}
            gap={8}
          >
            <Box
              display={"flex"}
              flexDirection={"column"}
              gap={4}
              className="comment-details-container"
            >
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <H8TitleMedium>Comment Information</H8TitleMedium>
              </Box>

              {/* Display Selected Cases */}
              <Box
                display="flex"
                flexDirection="row"
                flexGrow={1}
              >
                <StyledFormControl colors={colors} fullWidth margin="none">
                  <StyledTextField colors={colors}
                    variant="outlined"
                    label="Selected Cases"
                    fullWidth
                    value={selectedCasesData?.map(caseItem => caseItem?.["Case Number"] || caseItem?.caseNo).join(", ") || "N/A"}
                    disabled
                  />
                </StyledFormControl>
              </Box>

              {/* Display Selected Serials */}
              {hasSerials && (
                <Box
                  display="flex"
                  flexDirection="row"
                  flexGrow={1}
                >
                  <StyledFormControl colors={colors} fullWidth margin="none">
                    <StyledTextField colors={colors}
                      variant="outlined"
                      label="Selected Serials"
                      fullWidth
                      value={selectedSerials.join(", ") || "N/A"}
                      disabled
                    />
                  </StyledFormControl>
                </Box>
              )}

              {/* Generate Techsupport Checkbox */}
              {hasSerials && (
                <Box
                  display="flex"
                  flexDirection="row"
                  flexGrow={1}
                >
                  <FormControlLabel
                    control={
                      <Controller
                        name="generateTechsupport"
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            {...field}
                            checked={field.value || false}
                            sx={{
                              color: colors?.primary?.main,
                              '&.Mui-checked': {
                                color: colors?.primary?.main,
                              },
                            }}
                          />
                        )}
                      />
                    }
                    label={
                      <BodyMedium>
                        Generate Techsupport file for Customer Success Team
                      </BodyMedium>
                    }
                  />
                </Box>
              )}

              <Box
                display="flex"
                flexDirection="row"
                flexGrow={1}
              >
                <StyledFormControl colors={colors} fullWidth margin="none">
                  <Controller
                    name="comment"
                    control={control}
                    rules={{
                      required: "Comment is required",
                      minLength: {
                        value: 1,
                        message: "Comment cannot be empty",
                      },
                      maxLength: {
                        value: 512,
                        message: "Comment must be less than 512 characters",
                      },
                    }}
                    render={({ field }) => (
                      <StyledTextField colors={colors}
                        {...field}
                        variant="outlined"
                        label="Comment"
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Enter your comment here..."
                        error={Boolean(errors.comment)}
                        helperText={
                          errors.comment
                            ? errors.comment.message
                            : `Enter your comment (${512 - (commentValue?.length || 0)} characters remaining)`
                        }
                      />
                    )}
                  />
                </StyledFormControl>
              </Box>

              <BodyMedium>
                <b>Note:</b> Your comment will be added to the case history and visible to all team members.
              </BodyMedium>
            </Box>

            <Box
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"flex-end"}
              gap={4}
              className="action-button-container"
            >
              <PrimaryButton colors={colors}
                type="submit"
                onClick={handleSubmit(onSubmit)}
                disabled={!isValid || isPending}
                endIcon={
                  isPending && (
                    <CircularProgress
                      size={24}
                      sx={{ color: colors.primary.main }}
                    />
                  )
                }
              >
                {isPending
                  ? "Adding..."
                  : selectedCases.length > 1 ? "Add Comments" : "Add Comment"}
              </PrimaryButton>
            </Box>
          </Box>
        </form>
      </DialogContent>
    </StyledDialog>
  );
};

export default AddCommentModal;
